import { useState } from "react";
import { Button, FormField, Modal, TextInput, ToggleChipButton } from "@a2z/ui";
import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import type { Approver } from "@/lib/api/types";
import { queryKeys, useApiQuery } from "@/shared/queries";
import {
  APPROVAL_LOCKED_CODE,
  APPROVAL_PASSWORD_CHANGE_REQUIRED_CODE,
  isApprovalRefusal,
  type CommandApproval,
} from "@/shared/invoices/invoiceCommands";
import type { ApprovalCommandType } from "../lib/managerApproval";

/**
 * D156 — «اعتماد المدير»: a manager's OWN password over ONE exception act at
 * this till (founder 2026-10-15, amending D27).
 *
 * The chips are `GET /restaurant/approvers` — the owner and every member
 * holding `RESTAURANT_EXCEPTION_APPROVE`, minus whoever is signed in here.
 * Confirm hands `{ approverSellerId, password }` to `onSubmit`, which sends the
 * command through the ordinary envelope with it attached. A refused password
 * keeps the sheet open, says so and CLEARS the field; a locked approver says
 * that instead, and so does a manager whose right password is still the
 * temporary one (they must sign in and replace it first). Nothing here is remembered: the password lives in this sheet's
 * state and dies with it, and the next act asks again.
 */
export function ManagerApprovalSheet({
  commandType,
  onCancel,
  onSubmit,
}: {
  commandType: ApprovalCommandType;
  onCancel: () => void;
  /** Sends the command with the approval; throws an approval refusal back. */
  onSubmit: (approval: CommandApproval) => Promise<void>;
}) {
  const t = useTranslate();
  const approvers = useApiQuery<Approver[]>(
    queryKeys.approvers,
    endpoints.restaurant.approvers,
  );
  const list = approvers.data ?? [];
  const [chosen, setChosen] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<
    "invalid" | "locked" | "passwordChangeRequired" | null
  >(null);
  // A single approver is chosen for the cashier; more than one asks.
  const approverSellerId = chosen ?? (list.length === 1 ? list[0].sellerId : null);
  const canConfirm =
    approverSellerId !== null && password.length > 0 && !submitting;

  const confirm = async () => {
    if (!approverSellerId || password.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ approverSellerId, password });
    } catch (failure) {
      if (!isApprovalRefusal(failure)) throw failure;
      // Never left in the field after a refusal — on a shared till the next
      // person to touch it must not find a manager's password typed in.
      setPassword("");
      // INVALID is one code for every reason (a wrong password, an approver
      // who may not approve) — the sheet cannot say which, by design.
      setError(
        failure.code === APPROVAL_LOCKED_CODE
          ? "locked"
          : failure.code === APPROVAL_PASSWORD_CHANGE_REQUIRED_CODE
            ? "passwordChangeRequired"
            : "invalid",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onCancel}
      size="sm"
      title={t((m) => m.managerApproval.title)}
      closeLabel={t((m) => m.common.close)}
      data-testid="manager-approval-sheet"
      footerActions={
        <>
          <Button
            variant="outline"
            size="touch"
            disabled={submitting}
            onClick={onCancel}
            data-testid="manager-approval-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            variant="primary"
            size="touch"
            className="font-extrabold"
            disabled={!canConfirm}
            isLoading={submitting}
            onClick={() => void confirm()}
            data-testid="manager-approval-confirm"
          >
            {t((m) => m.managerApproval.confirm)}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (canConfirm) void confirm();
        }}
      >
        <p className="text-[length:var(--font-size-sm)] text-[color:var(--text-muted)]" data-testid="manager-approval-act">
          {t((m) => m.managerApproval[`intro_${commandType}`])}
        </p>

        <div>
          <span className="mb-1.5 block text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
            {t((m) => m.managerApproval.approverLabel)}
          </span>
          {approvers.isLoading ? (
            <p className="text-[length:var(--font-size-xs)] text-[color:var(--text-muted)]">
              {t((m) => m.managerApproval.loading)}
            </p>
          ) : list.length === 0 ? (
            <p
              role="alert"
              className="text-[length:var(--font-size-xs)] font-bold text-[color:var(--warning)]"
              data-testid="manager-approval-none"
            >
              {t((m) => m.managerApproval.noApprovers)}
            </p>
          ) : (
            <div
              className="flex flex-wrap gap-1.5"
              role="group"
              aria-label={t((m) => m.managerApproval.approverLabel)}
            >
              {list.map((approver) => (
                <ToggleChipButton
                  key={approver.sellerId}
                  active={approverSellerId === approver.sellerId}
                  aria-pressed={approverSellerId === approver.sellerId}
                  disabled={submitting}
                  data-testid={`manager-approval-approver-${approver.sellerId}`}
                  onClick={() => {
                    setChosen(approver.sellerId);
                    setError(null);
                  }}
                >
                  <bdi>{approver.name}</bdi>
                </ToggleChipButton>
              ))}
            </div>
          )}
        </div>

        <FormField
          name="managerApprovalPassword"
          label={t((m) => m.managerApproval.passwordLabel)}
          error={
            error === "invalid"
              ? t((m) => m.managerApproval.invalid)
              : error === "locked"
                ? t((m) => m.managerApproval.locked)
                : error === "passwordChangeRequired"
                  ? t((m) => m.managerApproval.passwordChangeRequired)
                  : undefined
          }
        >
          <TextInput
            name="managerApprovalPassword"
            type="password"
            autoComplete="off"
            dir="ltr"
            value={password}
            disabled={submitting}
            onChange={(event) => {
              setPassword(event.target.value);
              if (error === "invalid") setError(null);
            }}
            data-testid="manager-approval-password"
          />
        </FormField>
        <p className="text-[length:var(--font-size-2xs)] text-[color:var(--text-muted)]">
          {t((m) => m.managerApproval.hint)}
        </p>
      </form>
    </Modal>
  );
}
